import React, { useState, useEffect } from 'react';
import { 
  Firestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { Functions } from 'firebase/functions';
import { ThemeUpdateRequest } from './AppManager';

interface AdminPanelProps {
  db: Firestore;
  functions: Functions;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  onBack: () => void;
  selectedOrganizationId: string;
  onBackToOrganizations: () => void;
  onViewPermissions?: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({
  db,
  functions,
  onError,
  onSuccess,
  setLoading,
  onBack,
  selectedOrganizationId,
  onBackToOrganizations,
  onViewPermissions
}) => {
  const [themeRequests, setThemeRequests] = useState<(ThemeUpdateRequest & { docId: string })[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<(ThemeUpdateRequest & { docId: string }) | null>(null);
  const [showRequestDetails, setShowRequestDetails] = useState(false);

  // Get organization name (you might want to fetch this from Firestore)
  const getOrganizationName = (): string => {
    return selectedOrganizationId;
  };

  // Load theme update requests
  useEffect(() => {
    const loadThemeRequests = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, 'themeUpdateRequests'),
          where('status', '==', 'pending')
        );
        const querySnapshot = await getDocs(q);
        
        const requests: (ThemeUpdateRequest & { docId: string })[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as ThemeUpdateRequest;
          
          // Handle Firestore Timestamp conversion
          let requestedAt: Date;
          if (data.requestedAt instanceof Timestamp) {
            requestedAt = data.requestedAt.toDate();
          } else if (data.requestedAt && typeof data.requestedAt === 'object' && 'toDate' in data.requestedAt) {
            // Handle Firestore Timestamp object
            requestedAt = (data.requestedAt as any).toDate();
          } else {
            // Fallback to Date constructor
            requestedAt = new Date(data.requestedAt);
          }
          
          requests.push({
            ...data,
            docId: doc.id,
            requestedAt: requestedAt
          });
        });

        // Sort by most recent first
        requests.sort((a, b) => b.requestedAt.getTime() - a.requestedAt.getTime());
        setThemeRequests(requests);
      } catch (err) {
        console.error('Error loading theme requests:', err);
        onError('Failed to load theme update requests.');
      } finally {
        setLoading(false);
      }
    };

    loadThemeRequests();
  }, [db, setLoading, onError]);

  // Handle request approval
  const handleApproveRequest = async (request: ThemeUpdateRequest & { docId: string }) => {
    setLoading(true);
    try {
      // Update the app's theme in the apps collection
      const appRef = doc(db, 'apps', request.appId);
      await updateDoc(appRef, {
        'theme.custom': request.themeData
      });

      // Update the request status
      const requestRef = doc(db, 'themeUpdateRequests', request.docId);
      await updateDoc(requestRef, {
        status: 'approved',
        approvedAt: new Date()
      });

      // Remove from local state
      setThemeRequests(prev => prev.filter(req => req.docId !== request.docId));
      setShowRequestDetails(false);
      setSelectedRequest(null);

      onSuccess(`Theme update request for "${request.appName}" has been approved and applied.`);
    } catch (err: any) {
      console.error('Error approving theme request:', err);
      onError(err.message || 'Failed to approve theme request.');
    } finally {
      setLoading(false);
    }
  };

  // Handle request rejection
  const handleRejectRequest = async (request: ThemeUpdateRequest & { docId: string }) => {
    setLoading(true);
    try {
      // Update the request status
      const requestRef = doc(db, 'themeUpdateRequests', request.docId);
      await updateDoc(requestRef, {
        status: 'rejected',
        rejectedAt: new Date()
      });

      // Remove from local state
      setThemeRequests(prev => prev.filter(req => req.docId !== request.docId));
      setShowRequestDetails(false);
      setSelectedRequest(null);

      onSuccess(`Theme update request for "${request.appName}" has been rejected.`);
    } catch (err: any) {
      console.error('Error rejecting theme request:', err);
      onError(err.message || 'Failed to reject theme request.');
    } finally {
      setLoading(false);
    }
  };

  // View request details
  const handleViewRequest = (request: ThemeUpdateRequest & { docId: string }) => {
    setSelectedRequest(request);
    setShowRequestDetails(true);
  };

  // Generate and download theme JSON for preview
  const handleDownloadThemePreview = (themeData: any) => {
    try {
      const themeFileContent = {
        customTheme: themeData
      };
      
      const fileContent = JSON.stringify(themeFileContent, null, 2);
      const fileName = `theme-preview-${Date.now()}.json`;
      
      const blob = new Blob([fileContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      
      onSuccess(`Theme preview file ${fileName} has been downloaded.`);
    } catch (err: any) {
      console.error('Error downloading theme preview:', err);
      onError('Failed to download theme preview.');
    }
  };

  return (
    <>
      <div className="applist-content-header">
        <h1 className="applist-page-title">Admin Panel</h1>
      </div>

      <div className="applist-content-body">
        <div className="adminpanel-section">
          <div className="adminpanel-section-header">
            <h3>Theme Update Requests</h3>
            <div className="adminpanel-request-count">
              {themeRequests.length} pending request{themeRequests.length !== 1 ? 's' : ''}
            </div>
          </div>

          {themeRequests.length === 0 ? (
            <div className="adminpanel-empty-state">
              <div className="adminpanel-empty-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <h4>No Pending Requests</h4>
              <p>There are currently no theme update requests waiting for approval.</p>
            </div>
          ) : (
            <div className="adminpanel-requests-grid">
              {themeRequests.map((request) => (
                <div key={request.docId} className="adminpanel-request-card">
                  <div className="adminpanel-request-header">
                    <h4>{request.appName}</h4>
                    <span className="adminpanel-request-status adminpanel-status-pending">Pending</span>
                  </div>
                  
                  <div className="adminpanel-request-details">
                    <div className="adminpanel-detail-item">
                      <label>App ID:</label>
                      <span>{request.appId}</span>
                    </div>
                    <div className="adminpanel-detail-item">
                      <label>Requested by:</label>
                      <span>{request.requestedBy}</span>
                    </div>
                    <div className="adminpanel-detail-item">
                      <label>Requested at:</label>
                      <span>{request.requestedAt.toLocaleDateString()} {request.requestedAt.toLocaleTimeString()}</span>
                    </div>
                  </div>

                  <div className="adminpanel-request-actions">
                    <button
                      type="button"
                      className="adminpanel-view-details-btn"
                      onClick={() => handleViewRequest(request)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      View Details
                    </button>
                    
                    <button
                      type="button"
                      className="adminpanel-approve-btn"
                      onClick={() => handleApproveRequest(request)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Approve
                    </button>
                    
                    <button
                      type="button"
                      className="adminpanel-reject-btn"
                      onClick={() => handleRejectRequest(request)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Request Details Modal */}
      {showRequestDetails && selectedRequest && (
        <div className="adminpanel-modal-overlay">
          <div className="adminpanel-modal-container">
            <div className="adminpanel-modal-content">
              <div className="adminpanel-modal-header">
                <h3>Theme Update Request Details</h3>
                <button 
                  type="button" 
                  className="adminpanel-modal-close"
                  onClick={() => setShowRequestDetails(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="adminpanel-modal-body">
                <div className="adminpanel-request-info">
                  <h4>Request Information</h4>
                  <div className="adminpanel-info-grid">
                    <div className="adminpanel-info-item">
                      <label>App Name:</label>
                      <span>{selectedRequest.appName}</span>
                    </div>
                    <div className="adminpanel-info-item">
                      <label>App ID:</label>
                      <span>{selectedRequest.appId}</span>
                    </div>
                    <div className="adminpanel-info-item">
                      <label>Requested by:</label>
                      <span>{selectedRequest.requestedBy}</span>
                    </div>
                    <div className="adminpanel-info-item">
                      <label>Requested at:</label>
                      <span>
                        {selectedRequest.requestedAt.toLocaleDateString()} at {selectedRequest.requestedAt.toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="adminpanel-theme-preview">
                  <h4>New Theme Preview</h4>
                  <div className="adminpanel-theme-preview-grid">
                    <div className="adminpanel-preview-item">
                      <span className="adminpanel-preview-label">Background:</span>
                      <div className="adminpanel-color-swatch" style={{ backgroundColor: selectedRequest.themeData.backgroundColor }}></div>
                      <span className="adminpanel-color-value">{selectedRequest.themeData.backgroundColor}</span>
                    </div>
                    <div className="adminpanel-preview-item">
                      <span className="adminpanel-preview-label">Text:</span>
                      <div className="adminpanel-color-swatch" style={{ backgroundColor: selectedRequest.themeData.textColor }}></div>
                      <span className="adminpanel-color-value">{selectedRequest.themeData.textColor}</span>
                    </div>
                    <div className="adminpanel-preview-item">
                      <span className="adminpanel-preview-label">Card:</span>
                      <div className="adminpanel-color-swatch" style={{ backgroundColor: selectedRequest.themeData.cardColor }}></div>
                      <span className="adminpanel-color-value">{selectedRequest.themeData.cardColor}</span>
                    </div>
                    <div className="adminpanel-preview-item">
                      <span className="adminpanel-preview-label">Topbar BG:</span>
                      <div className="adminpanel-color-swatch" style={{ backgroundColor: selectedRequest.themeData.topbarBackgroundColor }}></div>
                      <span className="adminpanel-color-value">{selectedRequest.themeData.topbarBackgroundColor}</span>
                    </div>
                    <div className="adminpanel-preview-item">
                      <span className="adminpanel-preview-label">Topbar Icon:</span>
                      <div className="adminpanel-color-swatch" style={{ backgroundColor: selectedRequest.themeData.topbarIconColor }}></div>
                      <span className="adminpanel-color-value">{selectedRequest.themeData.topbarIconColor}</span>
                    </div>
                    <div className="adminpanel-preview-item">
                      <span className="adminpanel-preview-label">Sidebar BG:</span>
                      <div className="adminpanel-color-swatch" style={{ backgroundColor: selectedRequest.themeData.sidebarBackgroundColor }}></div>
                      <span className="adminpanel-color-value">{selectedRequest.themeData.sidebarBackgroundColor}</span>
                    </div>
                    <div className="adminpanel-preview-item">
                      <span className="adminpanel-preview-label">Cart BG:</span>
                      <div className="adminpanel-color-swatch" style={{ backgroundColor: selectedRequest.themeData.cartBackgroundColor }}></div>
                      <span className="adminpanel-color-value">{selectedRequest.themeData.cartBackgroundColor}</span>
                    </div>
                    <div className="adminpanel-preview-item">
                      <span className="adminpanel-preview-label">Pay Button:</span>
                      <div className="adminpanel-color-swatch" style={{ backgroundColor: selectedRequest.themeData.payButtonColor }}></div>
                      <span className="adminpanel-color-value">{selectedRequest.themeData.payButtonColor}</span>
                    </div>
                  </div>
                </div>

                <div className="adminpanel-theme-actions">
                  <button
                    type="button"
                    className="adminpanel-download-preview-btn"
                    onClick={() => handleDownloadThemePreview(selectedRequest.themeData)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download Theme JSON
                  </button>
                </div>
              </div>
              
              <div className="adminpanel-modal-footer">
                <button
                  type="button"
                  className="adminpanel-approve-btn"
                  onClick={() => handleApproveRequest(selectedRequest)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Approve & Apply Theme
                </button>
                <button
                  type="button"
                  className="adminpanel-reject-btn"
                  onClick={() => handleRejectRequest(selectedRequest)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Reject Request
                </button>
                <button
                  type="button"
                  className="adminpanel-cancel-btn"
                  onClick={() => setShowRequestDetails(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPanel;